import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { usesSidebarLayout } from "../utils/layout_media"

// Top-level controller for the Files app page (FILES_DESIGN_SPEC.md):
// multi-select + the floating bulk selection bar, the full-surface drag
// overlay (principle 1 - "the permanent dropzone is gone, just the Upload
// button plus a drop overlay only while dragging"), and opening/closing the
// right-hand preview pane. One controller rather than several cooperating
// ones because all three features share state that only makes sense at the
// page level - which rows are selected, which file is previewed - rather
// than on any one row.
//
// The preview pane is fetched as a server-rendered HTML fragment (not JSON)
// from UserFilesController#show's format.html branch, and reuses the exact
// same files-row / files-tags / split-menu controllers a list row uses -
// Stimulus connects controllers on newly-injected HTML automatically (the
// same mechanism Turbo Streams rely on), so nothing here re-implements
// rename/move/pin/delete/tag-edit for the pane.
//
// Desktop split layout: the pane is the shared .detail-sidebar (Network/
// Messages chrome - see _desktop_nav.scss, sidebar_resize.js), rendered once
// as a static <aside> by index.html.slim's content_for :phone_sidebar - a
// SIBLING of this controller's own .files-app element (both hang off
// .assistant-phone), not a descendant of it. Stimulus targets/actions only
// resolve within a controller's own subtree, so the pane's close button and
// dataset can't be reached with static/data-action wiring the way the rest
// of this controller works - they're wired here with plain DOM APIs instead
// (document.querySelector + addEventListener), the same bridge
// shared/_jane_sidebar and its header toggle use for the same reason.
// Phone width: .detail-sidebar is display:none outside the split-layout
// media query, and Files has no standalone per-file page to fall back to
// (UserFilesController#show always renders just the fragment) - so narrow
// viewports reuse the .email-modal-overlay dialog contact/message details
// fall back to (detail_modal_controller.js), built here the same way.
export default class extends Controller {
    static targets = ["dropOverlay", "rowCheckbox", "selectAll", "bulkBar", "bulkCount", "bulkTagInput"]
    static values = { filesBase: String }

    connect() {
        this.selected = new Set()
        this._dragDepth = 0
        this._activeFileId = null
        this._onKeydown = (event) => {
            if (event.key === "Escape" && this._activeFileId) this.closePreview()
        }
        // The tags controller (inside the fetched fragment) dispatches this
        // on a successful add/remove - see files_tags_controller.js. Stimulus
        // events bubble to `document` regardless of where the dispatching
        // element sits, so this catches it even though the pane itself is
        // outside .files-app's subtree.
        this._onTagsChanged = () => this.refreshPreview()
        // Crossing the split-layout breakpoint invalidates whichever
        // presentation is showing (sidebar vs. modal) - close rather than try
        // to re-render the other one mid-view, same guard
        // detail_modal_controller.js's own resize listener applies.
        this._onResize = () => {
            if (!this._activeFileId) return
            if (usesSidebarLayout() === !this._overlay) return
            this.closePreview()
        }
        this._bindPreviewClose()
        document.addEventListener("keydown", this._onKeydown)
        document.addEventListener("files-tags:changed", this._onTagsChanged)
        window.addEventListener("resize", this._onResize)
    }

    disconnect() {
        document.removeEventListener("keydown", this._onKeydown)
        document.removeEventListener("files-tags:changed", this._onTagsChanged)
        window.removeEventListener("resize", this._onResize)
        this._closeModal()
    }

    stopPropagation(event) {
        event.stopPropagation()
    }

    // --- Drag overlay -----------------------------------------------------

    dragenter(event) {
        event.preventDefault()
        this._dragDepth += 1
        if (this.hasDropOverlayTarget) this.dropOverlayTarget.hidden = false
    }

    dragover(event) {
        // Required for `drop` to fire at all.
        event.preventDefault()
    }

    dragleave(event) {
        event.preventDefault()
        this._dragDepth = Math.max(0, this._dragDepth - 1)
        if (this._dragDepth === 0 && this.hasDropOverlayTarget) this.dropOverlayTarget.hidden = true
    }

    drop(event) {
        event.preventDefault()
        this._dragDepth = 0
        if (this.hasDropOverlayTarget) this.dropOverlayTarget.hidden = true

        const files = event.dataTransfer && event.dataTransfer.files
        if (files && files.length) {
            // Decoupled from file_upload_controller.js by design - the drop
            // target is the whole page, but the upload button (and its
            // corner tray) live in the toolbar's own controller instance.
            document.dispatchEvent(new CustomEvent("files:dropped", { detail: { files } }))
        }
    }

    // --- Selection + bulk bar ---------------------------------------------

    toggleSelection(event) {
        const id = event.currentTarget.dataset.fileId
        if (!id) return
        if (event.currentTarget.checked) this.selected.add(id)
        else this.selected.delete(id)
        this._syncBulkBar()
    }

    toggleSelectAll(event) {
        const checked = event.currentTarget.checked
        this.rowCheckboxTargets.forEach((box) => {
            box.checked = checked
            const id = box.dataset.fileId
            if (!id) return
            if (checked) this.selected.add(id)
            else this.selected.delete(id)
        })
        this._syncBulkBar()
    }

    clearSelection() {
        this.selected.clear()
        this.rowCheckboxTargets.forEach((box) => { box.checked = false })
        if (this.hasSelectAllTarget) this.selectAllTarget.checked = false
        this._syncBulkBar()
    }

    bulkMove(event) {
        event.preventDefault()
        const folderId = event.currentTarget.dataset.folderId || ""
        const ids = this._selectedIds()
        if (!ids.length) return

        Promise.all(ids.map((id) => this._patchFile(id, { user_folder_id: folderId || null })))
            .then(() => window.location.reload())
    }

    bulkTag(event) {
        event.preventDefault()
        if (!this.hasBulkTagInputTarget) return
        const name = this.bulkTagInputTarget.value.trim()
        const ids = this._selectedIds()
        if (!name || !ids.length) return

        Promise.all(ids.map((id) => this._postTag(id, name))).then(() => window.location.reload())
    }

    // Triggers one download per selected file via a throwaway anchor click,
    // staggered slightly - clicking several download links in the same tick
    // gets some of them silently blocked as "multiple downloads" by the
    // browser, so this spaces them out instead of firing all at once.
    bulkDownload(event) {
        event.preventDefault()
        this._selectedIds().forEach((id, index) => {
            const box = this.rowCheckboxTargets.find((b) => b.dataset.fileId === id)
            const url = box && box.dataset.downloadUrl
            if (!url) return

            window.setTimeout(() => {
                const a = document.createElement("a")
                a.href = url
                a.style.display = "none"
                document.body.appendChild(a)
                a.click()
                a.remove()
            }, index * 350)
        })
    }

    bulkDelete(event) {
        event.preventDefault()
        const ids = this._selectedIds()
        if (!ids.length) return
        if (!window.confirm(`Delete ${ids.length} file${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return

        Promise.all(ids.map((id) => this._deleteFile(id))).then(() => window.location.reload())
    }

    _selectedIds() {
        return Array.from(this.selected)
    }

    _syncBulkBar() {
        if (!this.hasBulkBarTarget) return
        const count = this.selected.size
        this.bulkBarTarget.hidden = count === 0
        if (this.hasBulkCountTarget) this.bulkCountTarget.textContent = `${count} selected`
    }

    // --- Preview pane -------------------------------------------------------

    // Opens the pane for the file the clicked row/card represents - unless
    // the click actually landed on a checkbox, a link, or a menu button
    // inside it, in which case that control's own behavior should win
    // instead of also opening the preview underneath it.
    openPreview(event) {
        if (event.target.closest("input, button, a")) return
        const id = event.currentTarget.dataset.fileId
        if (id) this._loadPreview(id)
    }

    // Re-fetches the pane's own fragment for whichever file is currently
    // open - the tag add/remove controller (files_tags_controller.js) fires
    // "files-tags:changed" after a successful mutation (caught by the
    // document-level listener from #connect), rather than patching the chip
    // list by hand.
    refreshPreview() {
        if (this._activeFileId) this._loadPreview(this._activeFileId)
    }

    closePreview() {
        this._activeFileId = null
        this._closeModal()
        const aside = this._previewEl()
        if (aside) {
            aside.classList.add("d-none")
            const content = aside.querySelector(".detail-sidebar-content")
            if (content) content.innerHTML = ""
        }
        document.querySelector(".assistant-phone")?.classList.remove("is-detail-open")
    }

    _previewEl() {
        return document.querySelector(".detail-sidebar--files")
    }

    // The pane's close button is static server-rendered markup (outside this
    // controller's subtree - see the class comment), so it's bound once here
    // instead of via data-action.
    _bindPreviewClose() {
        const closeBtn = this._previewEl()?.querySelector(".detail-sidebar-close")
        if (closeBtn) closeBtn.addEventListener("click", () => this.closePreview())
    }

    _loadPreview(id) {
        fetch(`${this.filesBaseValue}/${id}`, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            .then((response) => response.text())
            .then((html) => {
                this._activeFileId = id
                if (usesSidebarLayout()) {
                    this._showSidebar(html)
                } else {
                    this._showModal(html)
                }
            })
            .catch(() => {})
    }

    _showSidebar(html) {
        this._closeModal()
        const aside = this._previewEl()
        if (!aside) return
        const content = aside.querySelector(".detail-sidebar-content")
        content.innerHTML = html
        aside.classList.remove("d-none")
        document.querySelector(".assistant-phone")?.classList.add("is-detail-open")
        aside.querySelector(".detail-sidebar-close")?.focus()
    }

    // Phone-width fallback: the same .email-modal-overlay dialog chrome
    // contact/message details use below the split-layout breakpoint
    // (detail_modal_controller.js) - reused as-is rather than inventing a
    // Files-specific narrow treatment.
    _showModal(html) {
        if (!this._overlay) {
            const overlay = document.createElement("div")
            overlay.className = "email-modal-overlay"
            overlay.innerHTML = `
                <div class="email-modal" role="dialog" aria-modal="true">
                    <button type="button" class="email-modal-close" aria-label="Close">
                        <i class="bi bi-x-lg"></i>
                    </button>
                    <div class="email-modal-content"></div>
                </div>`
            overlay.addEventListener("click", (event) => {
                if (event.target === overlay || event.target.closest(".email-modal-close")) this.closePreview()
            })
            document.body.appendChild(overlay)
            document.body.classList.add("email-modal-open")
            this._overlay = overlay
        }
        this._overlay.querySelector(".email-modal-content").innerHTML = html
        this._overlay.querySelector(".email-modal-close")?.focus()
    }

    _closeModal() {
        if (!this._overlay) return
        this._overlay.remove()
        this._overlay = null
        document.body.classList.remove("email-modal-open")
    }

    _patchFile(id, body) {
        return fetch(`${this.filesBaseValue}/${id}`, {
            method: "PATCH",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify(body)
        }).then((response) => response.json()).catch(() => ({ ok: false }))
    }

    _postTag(id, name) {
        return fetch(`${this.filesBaseValue}/${id}/tags`, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ name })
        }).then((response) => response.json()).catch(() => ({ ok: false }))
    }

    _deleteFile(id) {
        return fetch(`${this.filesBaseValue}/${id}`, {
            method: "DELETE",
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        }).then((response) => response.json()).catch(() => ({ ok: false }))
    }
}
